import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { actionIcons } from '../utils/iconMap';
import { Eye, Copy, Check } from 'lucide-react';
import apiClient from '../utils/axios';
import VariationManager from '../components/VariationManager';
import LeadPreviewModal from '../components/LeadPreviewModal';

export default function Templates() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewTemplateId, setPreviewTemplateId] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [copiedTemplateId, setCopiedTemplateId] = useState(null);

  useEffect(() => {
    fetchTemplates();
  }, []);


  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/templates');
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      // Show error to user
      if (error.response?.status === 401) {
        // Token expired, redirect to login
        window.location.href = '/login';
      } else {
        alert(`Error loading templates: ${error.response?.data?.message || error.message}`);
      }
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (templateId) => {
    try {
      await apiClient.put(`/templates/${templateId}/approve`, {});
      fetchTemplates();
    } catch (error) {
      console.error('Error approving template:', error);
      alert('Error approving template');
    }
  };

  const handleReject = async (templateId) => {
    if (!confirm('Are you sure you want to reject this template?')) {
      return;
    }
    
    try {
      await apiClient.put(`/templates/${templateId}/reject`, {});
      fetchTemplates();
    } catch (error) {
      console.error('Error rejecting template:', error);
      alert('Error rejecting template');
    }
  };

  // Helper function to safely format percentage
  const formatPercent = (value) => {
    if (value == null || value === '') return '0.0';
    const num = typeof value === 'string' ? parseFloat(value) : Number(value);
    return isNaN(num) ? '0.0' : num.toFixed(1);
  };

  // Function to copy HTML content to clipboard
  const handleCopyHTML = async (template) => {
    if (!template.html_content) {
      alert('No HTML content available for this template');
      return;
    }

    try {
      await navigator.clipboard.writeText(template.html_content);
      setCopiedTemplateId(template.id);
      // Reset the copied state after 2 seconds
      setTimeout(() => {
        setCopiedTemplateId(null);
      }, 2000);
    } catch (error) {
      console.error('Error copying HTML:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = template.html_content;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopiedTemplateId(template.id);
        setTimeout(() => {
          setCopiedTemplateId(null);
        }, 2000);
      } catch (err) {
        alert('Failed to copy HTML. Please try again.');
      }
      document.body.removeChild(textArea);
    }
  };

  return (
    <div>
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Email Templates</h1>

      {loading ? (
        <div className="text-center p-8">Loading templates...</div>
      ) : templates.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          No templates found. Approve articles to generate templates.
        </div>
      ) : (
        <div className="space-y-4">
          {templates.map((template) => (
            <div key={template.id} className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{template.name}</h3>
                  <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                    <span>Status: {template.status}</span>
                    <span>Sends: {template.sends_count || 0}</span>
                    <span>Open Rate: {formatPercent(template.open_rate)}%</span>
                    <span>Click Rate: {formatPercent(template.click_rate)}%</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => navigate(`/templates/editor/${template.id}`)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
                  >
                    {actionIcons.edit && <actionIcons.edit size={16} />}
                    Edit
                  </button>
                  <button
                    onClick={() => {
                      setPreviewTemplateId(template.id);
                      setPreviewModalOpen(true);
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center gap-2"
                  >
                    <Eye size={16} />
                    Preview as Lead
                  </button>
                  <button
                    onClick={() => setSelectedTemplate(selectedTemplate === template.id ? null : template.id)}
                    className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 flex items-center gap-2"
                  >
                    {actionIcons.sparkles && <actionIcons.sparkles size={16} />}
                    {selectedTemplate === template.id ? 'Hide Variations' : 'View Variations'}
                  </button>
                  <button
                    onClick={() => handleCopyHTML(template)}
                    className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 flex items-center gap-2"
                    title="Copy HTML code"
                  >
                    {copiedTemplateId === template.id ? (
                      <>
                        <Check size={16} />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy size={16} />
                        Copy HTML
                      </>
                    )}
                  </button>
                  {template.status === 'draft' && (
                    <>
                      <button
                        onClick={() => handleApprove(template.id)}
                        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => handleReject(template.id)}
                        className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                      >
                        Reject
                      </button>
                    </>
                  )}
                </div>
              </div>

              {template.subject_variants && template.subject_variants.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Subject Lines:</p>
                  <ul className="list-disc list-inside text-sm text-gray-600">
                    {template.subject_variants.map((subject, idx) => (
                      <li key={idx}>{subject}</li>
                    ))}
                  </ul>
                </div>
              )}

              {previewData && previewData.template.id === template.id ? (
                <div className="mt-4 border-t pt-4">
                  <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm font-medium text-blue-900 mb-2">
                      Previewing as: <strong>{previewData.lead.first_name} {previewData.lead.last_name}</strong> ({previewData.lead.email})
                    </p>
                    <p className="text-xs text-blue-700">Company: {previewData.lead.company_name || 'N/A'}</p>
                    <p className="text-xs text-blue-700">Title: {previewData.lead.job_title || 'N/A'}</p>
                    <p className="text-xs text-blue-700 font-semibold mt-2">
                      Sending Domain: <strong className="text-blue-900">{previewData.sending_domain || 'ucaasreview.com'}</strong>
                    </p>
                    <button
                      onClick={() => setPreviewData(null)}
                      className="mt-2 text-xs text-blue-600 hover:text-blue-800 underline"
                    >
                      Clear Preview
                    </button>
                  </div>
                  <div className="bg-gray-50 rounded p-4 border border-gray-200">
                    <div className="mb-2 text-sm font-semibold text-gray-700">Subject: {previewData.template.personalized_subject}</div>
                    <div dangerouslySetInnerHTML={{ __html: previewData.template.personalized_html }} />
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => {
                    const newSelected = selectedTemplate === `preview-${template.id}` ? null : `preview-${template.id}`;
                    setSelectedTemplate(newSelected);
                  }}
                  className="text-blue-600 hover:text-blue-800 text-sm"
                >
                  {selectedTemplate === `preview-${template.id}` ? 'Hide Template Preview' : 'Show Template Preview'}
                </button>
              )}

              {selectedTemplate === `preview-${template.id}` && !previewData && (
                <div className="mt-4 border-t pt-4">
                  <div className="bg-gray-50 rounded p-4">
                    <div dangerouslySetInnerHTML={{ __html: template.html_content }} />
                  </div>
                </div>
              )}

              {selectedTemplate === template.id && (
                <div className="mt-6 border-t pt-6">
                  <VariationManager 
                    templateId={template.id}
                    onVariationGenerated={() => fetchTemplates()}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <LeadPreviewModal
        templateId={previewTemplateId}
        isOpen={previewModalOpen}
        onClose={() => {
          setPreviewModalOpen(false);
          setPreviewTemplateId(null);
        }}
        onPreview={(data) => {
          setPreviewData(data);
          setPreviewModalOpen(false);
        }}
      />
    </div>
  );
}
